import React, { useEffect, useState } from "react";
import { usePermissions } from "../../PermissionsProvider";
import { EvaluationCriteriaService } from "../../services/EvaluationCriteriaService";
import {
  EvaluationCriteria,
  EvaluationType,
} from "../../model_gen/evaluation";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import {
  PlusIcon,
  PencilIcon,
  Trash2Icon,
  CopyIcon,
  LockIcon,
  Loader2Icon,
  ChevronDownIcon,
  ChevronRightIcon,
  ShieldCheckIcon,
  BrainCircuitIcon,
} from "lucide-react";
import CriteriaEditModal from "./CriteriaEditModal";
import { toast } from "sonner";
import { cn } from "../../lib/utils";
import { isDeterministicType, useDeterministicTypes } from "../../utils/evaluationTypes";

const FRAMEWORK_LABELS: Record<string, string> = {
  MAITAI_CLASSIC: "Maitai Classic",
  G_EVAL: "G-Eval",
  RAGAS: "RAGAS",
  FLASK: "FLASK",
};

function scoringBadgeText(crit: EvaluationCriteria): string {
  const cfg = crit.config as Record<string, any> | undefined;
  const scoringType = cfg?.scoring_type;
  if (scoringType === "boolean") return "Boolean";
  if (scoringType === "tags") {
    const count = cfg?.tags?.length ?? 0;
    return `Tags (${count})`;
  }
  if (scoringType === "numeric") {
    const min = cfg?.min ?? 1;
    const max = cfg?.max ?? 5;
    return `${min}–${max}`;
  }
  if (isDeterministicType(crit.type || "")) return "Pass / Fail";
  return "—";
}

const CriteriaPage: React.FC = () => {
  const { apiKey } = usePermissions();
  const deterministicTypes = useDeterministicTypes(apiKey);
  const [criteria, setCriteria] = useState<EvaluationCriteria[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCriteria, setEditingCriteria] =
    useState<EvaluationCriteria | null>(null);
  const [cloningCriteria, setCloningCriteria] =
    useState<EvaluationCriteria | null>(null);
  const [deleting, setDeleting] = useState<number | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(
    new Set(["DETERMINISTIC"])
  );

  const loadCriteria = () => {
    return EvaluationCriteriaService.list(setLoading, apiKey).subscribe(
      (data: EvaluationCriteria[]) => setCriteria(data),
      (err: any) => console.error("Failed to load criteria", err)
    );
  };

  useEffect(() => {
    const sub = loadCriteria();
    return () => sub.unsubscribe();
  }, [apiKey]);

  const handleEdit = (crit: EvaluationCriteria) => {
    setEditingCriteria(crit);
    setCloningCriteria(null);
    setModalOpen(true);
  };

  const handleClone = (crit: EvaluationCriteria) => {
    setCloningCriteria(crit);
    setEditingCriteria(null);
    setModalOpen(true);
  };

  const handleNew = () => {
    setEditingCriteria(null);
    setCloningCriteria(null);
    setModalOpen(true);
  };

  const handleDelete = (crit: EvaluationCriteria) => {
    if (!crit.id) return;
    setDeleting(crit.id);
    EvaluationCriteriaService.deleteCriteria(
      crit.id,
      () => {},
      apiKey
    ).subscribe(
      () => {
        setCriteria((prev) => prev.filter((c) => c.id !== crit.id));
        toast.success(`Deleted "${crit.name}"`);
        setDeleting(null);
      },
      (err: any) => {
        console.error("Failed to delete criteria", err);
        toast.error("Failed to delete criterion");
        setDeleting(null);
      }
    );
  };

  const handleModalSave = (saved: EvaluationCriteria) => {
    if (editingCriteria) {
      setCriteria((prev) =>
        prev.map((c) => (c.id === saved.id ? saved : c))
      );
    } else {
      setCriteria((prev) => [...prev, saved]);
    }
    setModalOpen(false);
    setEditingCriteria(null);
    setCloningCriteria(null);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setEditingCriteria(null);
    setCloningCriteria(null);
  };

  const toggleGroup = (groupKey: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupKey)) next.delete(groupKey);
      else next.add(groupKey);
      return next;
    });
  };

  const deterministicCriteria = criteria.filter(
    (c) => c.is_system && isDeterministicType(c.type || "")
  );

  const frameworkGroups: { key: string; label: string; items: EvaluationCriteria[] }[] = [];
  const frameworkOrder = ["MAITAI_CLASSIC", "G_EVAL", "RAGAS", "FLASK"];
  for (const fw of frameworkOrder) {
    const items = criteria.filter(
      (c) => c.is_system && c.type === EvaluationType.LLM_JUDGE && c.framework === fw
    );
    if (items.length > 0) {
      frameworkGroups.push({
        key: fw,
        label: FRAMEWORK_LABELS[fw] || fw,
        items,
      });
    }
  }

  const customCriteria = criteria.filter((c) => !c.is_system);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-gc-demi-bold text-foreground">
            Evaluation Criteria
          </h1>
          <p className="text-sm text-muted-foreground font-gc-reg mt-1">
            Manage the criteria used to evaluate test run results. System
            criteria are read-only but can be cloned.
          </p>
        </div>
        <Button onClick={handleNew} className="gap-2">
          <PlusIcon className="w-4 h-4" />
          New Criterion
        </Button>
      </div>

      {/* System Criteria */}
      <div className="space-y-3">
        <h2 className="text-sm font-gc-med text-muted-foreground uppercase tracking-wider flex items-center gap-2">
          <LockIcon className="w-3.5 h-3.5" />
          System Criteria
        </h2>

        {/* Deterministic */}
        {deterministicCriteria.length > 0 && (
          <CriteriaGroup
            groupKey="DETERMINISTIC"
            label="Objective (Deterministic)"
            icon={<ShieldCheckIcon className="w-4 h-4 text-blue-500" />}
            count={deterministicCriteria.length}
            expanded={expandedGroups.has("DETERMINISTIC")}
            onToggle={toggleGroup}
          >
            {deterministicCriteria.map((crit) => (
              <SystemCriteriaRow
                key={crit.id}
                crit={crit}
                onClone={handleClone}
              />
            ))}
          </CriteriaGroup>
        )}

        {/* Framework groups */}
        {frameworkGroups.map((group) => (
          <CriteriaGroup
            key={group.key}
            groupKey={group.key}
            label={group.label}
            icon={<BrainCircuitIcon className="w-4 h-4 text-purple-500" />}
            count={group.items.length}
            expanded={expandedGroups.has(group.key)}
            onToggle={toggleGroup}
          >
            {group.items.map((crit) => (
              <SystemCriteriaRow
                key={crit.id}
                crit={crit}
                onClone={handleClone}
              />
            ))}
          </CriteriaGroup>
        ))}
      </div>

      {/* Custom Criteria */}
      <div className="space-y-3">
        <h2 className="text-sm font-gc-med text-muted-foreground uppercase tracking-wider flex items-center gap-2">
          <BrainCircuitIcon className="w-3.5 h-3.5" />
          Custom Criteria
        </h2>

        <Card className="border-border">
          <CardContent className="p-0">
            {loading && customCriteria.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <Loader2Icon className="w-5 h-5 animate-spin text-muted-foreground" />
              </div>
            ) : customCriteria.length === 0 ? (
              <div className="text-center py-10 px-4">
                <p className="text-sm text-muted-foreground font-gc-reg">
                  No custom criteria yet. Click "New Criterion" to create one,
                  or clone a system criterion above.
                </p>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {customCriteria.map((crit) => (
                  <CustomCriteriaRow
                    key={crit.id}
                    crit={crit}
                    deleting={deleting}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <CriteriaEditModal
        open={modalOpen}
        onClose={handleModalClose}
        onSave={handleModalSave}
        criteria={editingCriteria}
        cloneFrom={cloningCriteria}
      />
    </div>
  );
};

/* ─── Collapsible group ─── */

interface CriteriaGroupProps {
  groupKey: string;
  label: string;
  icon: React.ReactNode;
  count: number;
  expanded: boolean;
  onToggle: (key: string) => void;
  children: React.ReactNode;
}

const CriteriaGroup: React.FC<CriteriaGroupProps> = ({
  groupKey,
  label,
  icon,
  count,
  expanded,
  onToggle,
  children,
}) => (
  <Card className="border-border overflow-hidden">
    <button
      onClick={() => onToggle(groupKey)}
      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-muted/40 transition-colors text-left"
    >
      {expanded ? (
        <ChevronDownIcon className="w-4 h-4 text-muted-foreground flex-shrink-0" />
      ) : (
        <ChevronRightIcon className="w-4 h-4 text-muted-foreground flex-shrink-0" />
      )}
      {icon}
      <span className="text-sm font-gc-med text-foreground">{label}</span>
      <span className="text-xs text-muted-foreground font-gc-reg ml-1">
        ({count})
      </span>
    </button>
    {expanded && (
      <CardContent className="p-0 border-t border-border">
        <div className="divide-y divide-border">{children}</div>
      </CardContent>
    )}
  </Card>
);

/* ─── System criteria row ─── */

interface SystemCriteriaRowProps {
  crit: EvaluationCriteria;
  onClone: (crit: EvaluationCriteria) => void;
}

const SystemCriteriaRow: React.FC<SystemCriteriaRowProps> = ({
  crit,
  onClone,
}) => {
  const isDet = isDeterministicType(crit.type || "");
  return (
    <div className="flex items-center gap-4 px-4 py-3 hover:bg-muted/30 transition-colors">
      <LockIcon className="w-3 h-3 text-muted-foreground/50 flex-shrink-0" />
      <span className="text-sm font-gc-med text-foreground w-40 min-w-[10rem] truncate flex-shrink-0">
        {crit.name}
      </span>
      {crit.instructions && (
        <span className="text-xs text-muted-foreground font-gc-reg flex-1 min-w-0 truncate">
          {crit.instructions}
        </span>
      )}
      {!crit.instructions && <span className="flex-1" />}
      <Badge
        variant="outline"
        className={cn(
          "flex-shrink-0 text-[10px]",
          isDet
            ? "text-blue-600 border-blue-200 bg-blue-50 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-800"
            : "text-purple-600 border-purple-200 bg-purple-50 dark:bg-purple-900/20 dark:text-purple-400 dark:border-purple-800"
        )}
      >
        {isDet ? "Objective" : "Subjective"}
      </Badge>
      <span className="text-xs text-muted-foreground font-gc-reg w-20 text-right flex-shrink-0">
        {scoringBadgeText(crit)}
      </span>
      <Button
        variant="ghost"
        size="sm"
        className="h-7 gap-1.5 text-xs text-muted-foreground hover:text-foreground flex-shrink-0"
        onClick={(e) => {
          e.stopPropagation();
          onClone(crit);
        }}
      >
        <CopyIcon className="w-3 h-3" />
        Clone
      </Button>
    </div>
  );
};

/* ─── Custom criteria row ─── */

interface CustomCriteriaRowProps {
  crit: EvaluationCriteria;
  deleting: number | null;
  onEdit: (crit: EvaluationCriteria) => void;
  onDelete: (crit: EvaluationCriteria) => void;
}

const CustomCriteriaRow: React.FC<CustomCriteriaRowProps> = ({
  crit,
  deleting,
  onEdit,
  onDelete,
}) => (
  <div className="flex items-center gap-4 px-4 py-3 hover:bg-muted/30 transition-colors">
    <span className="text-sm font-gc-med text-foreground w-40 min-w-[10rem] truncate flex-shrink-0">
      {crit.name}
    </span>
    {crit.instructions && (
      <span className="text-xs text-muted-foreground font-gc-reg flex-1 min-w-0 truncate">
        {crit.instructions}
      </span>
    )}
    {!crit.instructions && <span className="flex-1" />}
    <span className="text-xs text-muted-foreground font-gc-reg w-20 text-right flex-shrink-0">
      {scoringBadgeText(crit)}
    </span>
    <div className="flex items-center gap-1 flex-shrink-0">
      <Button
        variant="ghost"
        size="sm"
        className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
        onClick={(e) => {
          e.stopPropagation();
          onEdit(crit);
        }}
      >
        <PencilIcon className="w-3.5 h-3.5" />
      </Button>
      <Button
        variant="ghost"
        size="sm"
        className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
        disabled={deleting !== null}
        onClick={(e) => {
          e.stopPropagation();
          onDelete(crit);
        }}
      >
        {deleting === crit.id ? (
          <Loader2Icon className="w-3.5 h-3.5 animate-spin" />
        ) : (
          <Trash2Icon className="w-3.5 h-3.5" />
        )}
      </Button>
    </div>
  </div>
);

export default CriteriaPage;
